import React, { useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { theme } from 'styled-tools'
import Image from '../../image'
import { extractUrlSettings } from '../../../../utils/cloudinary'
import useTranslation from '../../../../utils/translation'
import CloseIcon from '../../icons/close'

const ImageTextWrap = styled.div`
  display: block;
  ${(props) => props.theme.media.min.sm`
    display: flex;;
  `}
  flex-direction: row;
  align-items: center;
  padding: 12px;
  box-sizing: content-box;
  overflow: hidden;
  justify-content: space-between;
  justify-content: ${theme('imageOption.justifyContent')};
  min-width: ${theme('imageOption.desktop.minWidth')};
  height: 100%;
  box-sizing: border-box;
  background-color: ${(props) =>
    theme('imageOption.imageForColor.backgroundColor')(props) ||
    theme('textOption.backgroundColor')(props)};

  ${(props) => props.theme.media.min.sm`
    width: ${theme('imageOption.wrap.desktop.width')};
  `}
  position: relative;
  background-color: #ffffff;
  border-radius: 5px;
  white-space: nowrap;
  box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.15);
  border: ${(props) =>
    props.$active ? '2px solid #192228' : '2px solid #ffffff'};
  cursor: auto;
`

const ImageContainer = styled.div`
  display: block;
  padding: 10px 25px 10px 10px;
`

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
`

const StyledImage = styled(Image)`
  max-width: 60px;
`

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
`
const RightContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 20px;
  ${(props) => props.theme.media.min.sm`
    margin-top: 0px;
  `}
`

const SelectButton = styled.div`
  border: 1px solid #192228;
  background-color: ${(props) => (props.$active ? '#192228' : '#ffffff')};
  color: ${(props) => (props.$active ? '#ffffff' : '#192228')};
  padding: 10px 10px;
  min-width: 110px;
  width: 100%;
  text-align: center;
  border-radius: 3px;
  font-size: 16px;
  font-weight: 500;
  margin-right: 0px;
  ${(props) => props.theme.media.min.sm`
    margin-right: 26px;
  `}
  cursor: pointer;
`

const Text = styled.div`
  line-height: 20px;
  font-family: ${theme('textOption.text.fontFamily')};
  font-size: 16px;
  font-weight: 500;
  color: #000000;
  white-space: pre-wrap;
  white-space: ${theme('textOption.text.whiteSpace')};
  text-align: center;
  text-align: ${theme('textOption.text.textAlign')};
  font-style: ${theme('textOption.text.fontStyle')};
  text-align: left;

  em {
    font-weight: 400;
  }

  p {
    margin-top: 0;
    margin-bottom: 0;
  }

  ${(props) => props.theme.media.min.sm`
    font-size: 18px;
  `}
`
const OverlayUrl = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  text-decoration: underline;
  margin-block-start: 0em;
  margin-block-end: 0em;
  margin-top: 5px;
  cursor: pointer;
  text-wrap: auto;
`

const OverlayContainer = styled.div`
  display: ${(props) => (props.visible ? 'block' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 2000;
  overflow: auto;
`
const OverlayContent = styled.div`
  background-color: #ffffff;
  color: #011d24;
  padding: 27px;
  position: fixed;
  top: 0;
  right: 0;
  max-width: 484px;
  width: 100%;
  height: 100%;
  overflow: auto;

  h2 {
    font-family: ${theme('typography.pageTitle.fontFamily')};
    font-size: 28px;
    font-weight: 500;
    line-height: 35px;
  }
  img {
    max-width: 430px;
    width: 100%;
    margin-bottom: 20px;
    aspect-ratio: 4 / 3;
    object-fit: cover;
  }
`
const OverlayScrollContent = styled.div`
  word-wrap: break-word;
  white-space: pre-wrap;
  word-break: break-word;
  h2 {
    margin-block-start: 0em;
    margin-block-end: 0em;
    margin-top: 15px;
    margin-bottom: 20px;
  }
`

const OverlayCloseIconContainer = styled.div`
  text-align: right;
  svg {
    max-width: 12px;
    cursor: pointer;
  }
`

const BoxedImageTextOption = ({
  name,
  image,
  text,
  active,
  questionStyle,
  modalDescription,
  modalHeading,
  modalImages,
}) => {
  const [isOverlayVisible, setOverlayVisible] = useState(false)

  const imageUrl = useMemo(() => {
    if (!image || !image.url) {
      return null
    }
    const url = image.url
    const publicId = image.publicId
    if (!publicId) {
      return url
    }

    let type = image.type
    if (!type) {
      const { deliveryType } = extractUrlSettings(url)
      type = deliveryType
    }

    return `https://assets.nobiadigital.com/image/${type}/c_scale,w_480/q_auto/f_auto/${publicId}`
  }, [image])

  const modalImageUrls = useMemo(
    () =>
      modalImages?.map((img) => {
        if (!img || !img.url) {
          return null
        }

        const url = img.url
        const publicId = img.publicId
        if (!publicId) {
          return url
        }

        let type = img.type
        if (!type) {
          const { deliveryType } = extractUrlSettings(url)
          type = deliveryType
        }

        return `https://assets.nobiadigital.com/image/${type}/c_scale,w_480/q_auto/f_auto/${publicId}`
      }) || [],
    [modalImages]
  )

  const selectButtonRef = useRef(null)

  const handleSelection = (e) => {
    if (!selectButtonRef.current.contains(e.target)) {
      e.stopPropagation()
    }
  }

  const toggleOverlay = () => {
    setOverlayVisible(!isOverlayVisible)
  }

  const t = useTranslation('app.prep-form')

  return (
    <ImageTextWrap $active={active} onClick={handleSelection}>
      <LeftContainer>
        {image && (
          <ImageContainer>
            <ImageWrapper $active={active}>
              {image && (
                <StyledImage
                  value={{ url: imageUrl, alt: name }}
                  sizes="40px"
                  questionStyle={questionStyle}
                />
              )}
            </ImageWrapper>
          </ImageContainer>
        )}
        <div>
          {text && (
            <Text $active={active} dangerouslySetInnerHTML={{ __html: text }} />
          )}
          <OverlayUrl onClick={toggleOverlay}>
            {t('view-example-image')}
          </OverlayUrl>
        </div>
      </LeftContainer>
      <RightContainer>
        <SelectButton ref={selectButtonRef} $active={active}>
          {active === true ? t('button-selected') : t('button-select')}
        </SelectButton>
      </RightContainer>
      <OverlayContainer visible={isOverlayVisible} onClick={toggleOverlay}>
        <OverlayContent>
          <OverlayScrollContent>
            <OverlayCloseIconContainer>
              <CloseIcon />
            </OverlayCloseIconContainer>
            <h2>{modalHeading}</h2>
            <p>{modalDescription}</p>
            {modalImageUrls.map((url, index) =>
              url ? (
                <img
                  key={modalImages[index].publicId || index}
                  src={url}
                  alt={`${name} image ${index + 1}`}
                />
              ) : null
            )}
          </OverlayScrollContent>
        </OverlayContent>
      </OverlayContainer>
    </ImageTextWrap>
  )
}

BoxedImageTextOption.defaultProps = {
  text: null,
  image: null,
  active: null,
  questionStyle: 'style1',
  modalHeading: null,
  modalDescription: null,
  modalImages: [],
}

BoxedImageTextOption.propTypes = {
  name: PropTypes.string.isRequired,
  text: PropTypes.string,
  image: PropTypes.shape({
    url: PropTypes.string,
    publicId: PropTypes.string,
    type: PropTypes.string,
  }),
  active: PropTypes.bool.isRequired,
  questionStyle: PropTypes.string,
  modalHeading: PropTypes.string,
  modalDescription: PropTypes.string,
  modalImages: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      publicId: PropTypes.string,
      type: PropTypes.string,
    })
  ),
}

export default BoxedImageTextOption
